import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { VirtualConnectionOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createLabelColumnFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GraphLabel,
  GridSortProps,
  reportGraphQLErrors,
  SiteUrlsContext,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
  LabelContent,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { VirtualConnectionsDisplayGridRowData } from '../database/types/VirtualConnectionDisplayGridRowData';
import { createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { GlobalAssetGridDC3ColumnFactoryParams } from '../sorting/makeColumnFactoryParams';
import { VirtualConnectionsValidSortFields } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';

const shouldBeObfuscated: (rowData: VirtualConnectionsDisplayGridRowData) => boolean = ({ name }) => name === null;
const projectContainerType = 'Project';

type VirtualConnectionsColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<VirtualConnectionsValidSortFields>,
  VirtualConnectionsDisplayGridRowData,
  VirtualConnectionsValidSortFields
>;



function columns(siteUrls: SiteUrls): ReadonlyArray<VirtualConnectionsColumnFactory> {
  const cols: ReadonlyArray<VirtualConnectionsColumnFactory> = [
    () => createLabelGridColumnFormatter(siteUrls, 'CERTIFICATION'),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: VirtualConnectionOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.virtualConnectionLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: VirtualConnectionOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamWorkbooksConnection }) =>
          downstreamWorkbooksConnection
            ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: VirtualConnectionOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamDatasourcesConnection }) =>
          downstreamDatasourcesConnection
            ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'location',
      dataKey: VirtualConnectionOrderField.CONTAINER_NAME,
      headerText: Messages.GridHeaderLocation(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<VirtualConnectionsColumnFactory>;
}

export type VirtualConnectionsDisplayGridProps = UsualDisplayGridProps<
  VirtualConnectionsDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<VirtualConnectionsValidSortFields>;

export function VirtualConnectionsDisplayGrid(props: VirtualConnectionsDisplayGridProps): JSX.Element {
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls).map((columnFactory) => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

function getLabelContentFromRowData(rowData: VirtualConnectionsDisplayGridRowData, urls: SiteUrls) {
  return {
    contentId: rowData.id,
    contentLuid: rowData.luid,
    contentType: rowData.contentType,
    contentName: rowData.name ?? undefined,
    contentUrl: urls.virtualConnectionLineageUrl(rowData.vizportalUrlId),
    subjectArea: rowData.__typename
  };
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<VirtualConnectionsDisplayGridRowData, VirtualConnectionsValidSortFields> {

  function getPropsFromRowData(rowData: VirtualConnectionsDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'virtualconnections', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<VirtualConnectionsDisplayGridRowData, VirtualConnectionsValidSortFields>;
}
